import {
  getPromotedPages,
  getPopularPages,
  getPagesNearYou,
  getActivePagesCount,
  MappedProject,
} from 'src/helpers/api/discovery';
import { AppState } from 'src/redux/modules';
import { Dispatch } from 'redux';

export interface HomeRequestAction {
  type: 'HOME_REQUEST';
}

export interface HomeSuccessAction {
  type: 'HOME_SUCCESS';
}

export interface HomeFailureAction {
  type: 'HOME_FAILURE';
  payload: string;
}

export type HomeActions =
  | HomeRequestAction
  | HomeSuccessAction
  | HomeFailureAction;

export const loadHome = () => async (dispatch: Dispatch<AppState>) => {
  const loadCarousel = dispatch<any>(loadCarouselPages());
  const loadFiltered = dispatch<any>(loadFilteredPages({ pageNo: 1 }));
  const loadActiveCount = dispatch<any>(loadActivePagesCount());

  try {
    dispatch({
      type: 'HOME_REQUEST',
    });
    await Promise.all([loadCarousel, loadFiltered, loadActiveCount]);
    dispatch({
      type: 'HOME_SUCCESS',
    });
  } catch (e) {
    dispatch({
      type: 'HOME_FAILURE',
      payload: e,
    });
  }
};

export interface CarouselRequestAction {
  type: 'CAROUSEL_PAGES_REQUEST';
}

export interface CarouselSuccessAction {
  type: 'CAROUSEL_PAGES_SUCCESS';
  payload: {
    items: MappedProject[];
  };
}

export interface CarouselFailureAction {
  type: 'CAROUSEL_PAGES_FAILURE';
}

export type CarouselActions =
  | CarouselRequestAction
  | CarouselSuccessAction
  | CarouselFailureAction;

const loadCarouselPages = () => async (dispatch: Dispatch<AppState>) => {
  dispatch({
    type: 'CAROUSEL_PAGES_REQUEST',
  });

  try {
    const data = await getPromotedPages();
    dispatch({
      type: 'CAROUSEL_PAGES_SUCCESS',
      payload: { ...data },
    });
  } catch (err) {
    dispatch({ type: 'CAROUSEL_PAGES_FAILURE', error: true, payload: err });
  }
};

export interface FilteredRequestAction {
  type: 'FILTERED_PAGES_REQUEST';
}

export interface FilteredSuccessAction {
  type: 'FILTERED_PAGES_SUCCESS';
  payload: {
    totalCount: number;
    pageNo: number;
    items: any;
  };
}

export interface FilteredFailureAction {
  type: 'FILTERED_PAGES_FAILURE';
}

export type FilteredActions =
  | FilteredRequestAction
  | FilteredSuccessAction
  | FilteredFailureAction;

export const CATEGORY_EVERYTHING_ID = 999;

export const loadFilteredPages = ({
  pageNo = 1,
  categoryId = CATEGORY_EVERYTHING_ID,
  locationId = 0,
}) => async (dispatch: Dispatch<AppState>) => {
  let analytics;

  if (pageNo > 1) {
    analytics = {
      event: 'click',
      subtype: 'button',
      event_value: 'see more',
      page_section: 'home > show me',
    };
  }

  dispatch({
    type: 'FILTERED_PAGES_REQUEST',
    payload: { pageNo, categoryId, locationId },
    meta: { analytics },
  });

  try {
    const data =
      !locationId || locationId === 0
        ? await getPopularPages(pageNo, categoryId)
        : await getPagesNearYou(pageNo, categoryId);

    dispatch({ type: 'FILTERED_PAGES_SUCCESS', payload: { ...data } });
  } catch (err) {
    dispatch({ type: 'FILTERED_PAGES_FAILURE', error: true, payload: err });
  }
};

const CATEGORY_PAGE_REQUEST = 'CATEGORY_PAGE_REQUEST';
const GET_STARTED_REQUEST = 'GET_STARTED_REQUEST';
const SEE_MORE_FAQ_REQUEST = 'SEE_MORE_FAQ_REQUEST';
const HOME_PAGE_CARD_SELECTED = 'HOME_PAGE_CARD_SELECTED';

interface Filter {
  categoryId: number;
  typeId: number;
  locationId: number;
}

export interface ChangeFilterAction {
  type: 'CHANGE_PAGES_FILTER';
  payload: {
    filter: Filter;
  };
}

export const changePagesFilter = (selection: any) => (
  dispatch: Dispatch<AppState>
) => {
  const { category, type, location } = selection;

  dispatch({
    type: 'CHANGE_PAGES_FILTER',
    payload: {
      filter: {
        categoryId: category.id,
        typeId: type.id,
        locationId: location.id,
      },
    },
    meta: {
      analytics: {
        event: 'click',
        subtype: 'button',
        event_value: `${category.value.toLowerCase()} > ${type.value.toLowerCase()} > ${location.value.toLowerCase()}`,
        page_section: 'home > show me',
      },
    },
  });

  if (type.id === 0) {
    dispatch<any>(loadCarouselPages());
  }

  dispatch<any>(
    loadFilteredPages({
      pageNo: 1,
      categoryId: category.id,
      locationId: location.id,
    })
  );
};

export interface ActivePageCountRequestAction {
  type: 'ACTIVE_PAGES_COUNT_REQUEST';
}

export interface ActivePageCountSuccessAction {
  type: 'ACTIVE_PAGES_COUNT_SUCCESS';
  payload: {
    activePagesCount: number;
  };
}

export interface ActivePageFailureAction {
  type: 'ACTIVE_PAGES_COUNT_FAILURE';
}

export type ActivePageActions =
  | ActivePageCountRequestAction
  | ActivePageCountSuccessAction
  | ActivePageFailureAction;

const loadActivePagesCount = () => async (dispatch: Dispatch<AppState>) => {
  dispatch({ type: 'ACTIVE_PAGES_COUNT_REQUEST' });
  try {
    const data = await getActivePagesCount();

    dispatch({
      type: 'ACTIVE_PAGES_COUNT_SUCCESS',
      payload: { activePagesCount: data },
    });
  } catch (err) {
    dispatch({ type: 'ACTIVE_PAGES_COUNT_FAILURE', error: true, payload: err });
  }
};

export const loadCategoryPage = ({ name }: { name: string }) => ({
  type: CATEGORY_PAGE_REQUEST,
  meta: {
    analytics: {
      event: 'link click',
      subtype: 'button',
      event_value: name.toLowerCase(),
      page_section: 'home',
    },
  },
});

export const getStarted = ({ location }: { location: string }) => ({
  type: GET_STARTED_REQUEST,
  meta: {
    analytics: {
      event: 'link click',
      subtype: 'button',
      event_value: 'get started',
      page_section: location,
    },
  },
});

export const seeMoreFaq = () => ({
  type: SEE_MORE_FAQ_REQUEST,
  meta: {
    analytics: {
      event: 'link click',
      subtype: 'button',
      event_value: 'see more',
      page_section: 'home > faqs',
    },
  },
});

interface SelectCardParams {
  id: string;
  type: string;
  url: string;
  rank: string;
  totalCount: string;
}

export const selectCard = ({
  id,
  type,
  url,
  rank,
  totalCount,
}: SelectCardParams) => ({
  type: HOME_PAGE_CARD_SELECTED,
  meta: {
    analytics: {
      event: 'click',
      subtype: 'card',
      event_value: 'crowdfunding card',
      page_section: 'home > show me',
      activity_id: id,
      activity_type: 'crowdfunding_guid',
      content_id: id,
      content_type: 'crowdfunding_guid',
      card_id: `${type}-${id}`,
      card_url: url,
      card_rank: rank,
      card_count: totalCount,
    },
  },
});



// WEBPACK FOOTER //
// ./src/redux/modules/home/actions.ts